import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { HeroBackground } from './HeroBackground';
import { ScrambleText } from './ScrambleText';
const WORDS = ['Fashion', 'Licensing', 'Creators', 'Brands'];
// plural subjects need "Move", not "Moves"
const PLURAL_WORDS = ['Creators', 'Brands'];
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&/\\';
/**
 * The trailing "s" of MOVES — decodes in/out with the same glyph-scramble
 * treatment as the rotating word. Scrambles then resolves to "s" for
 * singular words, or scrambles then vanishes for plural words.
 */
function ScrambleS({
  show
}: {
  show: boolean;
}) {
  const [display, setDisplay] = useState(show ? 's' : '');
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      setDisplay(show ? 's' : '');
      return;
    }
    let frame = 0;
    const maxFrame = 8;
    const id = window.setInterval(() => {
      frame += 1;
      if (frame >= maxFrame) {
        setDisplay(show ? 's' : '');
        window.clearInterval(id);
      } else {
        setDisplay(GLYPHS[Math.floor(Math.random() * GLYPHS.length)]);
      }
    }, 45);
    return () => window.clearInterval(id);
  }, [show]);
  return <span aria-hidden="true" className="inline-block" data-id="element-110">
      {display}
    </span>;
}
type HeroProps = {
  word?: string;
};
export function Hero({
  word
}: HeroProps) {
  const [index, setIndex] = useState(0);
  // auto-cycle unless a specific word is forced from outside
  useEffect(() => {
    if (word) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % WORDS.length);
    }, 2800);
    return () => clearInterval(id);
  }, [word]);
  const current = word ?? WORDS[index];
  const isPlural = PLURAL_WORDS.includes(current);
  return <section id="top" className="relative flex min-h-screen w-full flex-col justify-center overflow-hidden bg-black px-5 pb-16 pt-32 sm:px-8 lg:px-12" data-id="element-111">
      <HeroBackground data-id="element-112" />

      {/* content sits on the left, clear of the floating rack on the right */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-start text-left" data-id="element-113">
        <div className="w-full max-w-2xl lg:max-w-3xl" data-id="element-114">
          {/* [DECODED WORD] THAT MOVE(S) CULTURE — left-aligned beside the clothes */}
          <motion.h1 className="font-display flex flex-col items-start uppercase leading-[1.1] tracking-[0.04em] text-white" initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-115">
            <span className="flex items-baseline gap-4 text-4xl sm:text-6xl md:text-7xl lg:text-8xl" data-id="element-116">
              <span className="relative inline-flex h-[1.1em] items-center overflow-hidden" data-id="element-117">
                <ScrambleText text={current} className="block" data-id="element-118" />
              </span>
              That
            </span>
            <span className="flex items-baseline text-4xl sm:text-6xl md:text-7xl lg:text-8xl" data-id="element-119">
              <span className="flex items-baseline" data-id="element-120">
                Move
                <ScrambleS show={!isPlural} data-id="element-121" />
              </span>
              <span className="ml-4" data-id="element-122">Culture</span>
            </span>
            <span className="sr-only" aria-live="polite" data-id="element-123">
              {current} that {isPlural ? 'move' : 'moves'} culture
            </span>
          </motion.h1>

          {/* CTA directly under the headline */}
          <motion.div className="mt-10" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.6
        }} data-id="element-124">
            <Link to="/contact" className="group inline-flex items-center gap-12 border border-white/70 px-14 py-6 text-sm font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-black sm:text-base" data-id="element-125">
              Work With Us
              <ArrowRightIcon className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-126" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>;
}